import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

/**
 * Rewards Wallet — public page opened from the WhatsApp URL button in the
 * `eden_reward_points_earned` template. Uses a signed JWT token; no login.
 *
 * Design language (Eden Brand Kit — Feb 2026):
 *  · Champagne Cream #F5F2EA background
 *  · Cormorant Garamond light for headlines · Manrope for body/UI
 *  · Champagne Gold #C5A059 borders at 25% opacity — no shadows, square corners
 *  · Emerald #059669 for the CTA state · Charcoal #1A1A1A for text
 *  · Generous whitespace, left-aligned, editorial rhythm
 */

const API = process.env.REACT_APP_BACKEND_URL;

const BRAND = {
  cream: '#F5F2EA',
  sand: '#EBE7DD',
  charcoal: '#1A1A1A',
  slate: '#4A4A4A',
  gold: '#C5A059',
  goldSoft: 'rgba(197, 160, 89, 0.25)',
  emerald: '#059669',
  emeraldDeep: '#047857',
  white: '#FFFFFF',
};

const fontHead = {
  fontFamily: "'Cormorant Garamond', 'Georgia', serif",
  fontWeight: 300,
  letterSpacing: '-0.01em',
  color: BRAND.charcoal,
};
const fontBody = {
  fontFamily: "'Manrope', 'Inter', system-ui, sans-serif",
  color: BRAND.charcoal,
};
const overlineStyle = {
  fontFamily: "'Manrope', system-ui, sans-serif",
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.2em',
  color: BRAND.gold,
};

const RewardWalletPublicPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    // Load the Google fonts once per mount
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400&family=Manrope:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    document.body.style.backgroundColor = BRAND.cream;
    return () => { document.body.style.backgroundColor = ''; };
  }, []);

  useEffect(() => {
    if (!token) { setErr('Missing link token'); setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/rewards/wallet-by-token/${encodeURIComponent(token)}`);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || 'Could not load your rewards wallet');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: BRAND.cream, ...fontBody, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ ...fontHead, fontSize: 32 }}>Loading your wallet…</div>
      </div>
    );
  }

  if (err) {
    return (
      <div style={{ minHeight: '100vh', background: BRAND.cream, ...fontBody, padding: '96px 24px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <div style={overlineStyle}>Rewards Wallet</div>
          <h1 style={{ ...fontHead, fontSize: 48, margin: '16px 0 24px' }}>Link unavailable</h1>
          <p style={{ ...fontBody, color: BRAND.slate, fontSize: 16, lineHeight: 1.7 }}>{err}</p>
          <p style={{ ...fontBody, color: BRAND.slate, fontSize: 14, lineHeight: 1.7, marginTop: 32 }}>
            Please contact the clinic at <a href="tel:+917710117777" style={{ color: BRAND.emerald, textDecoration: 'none', fontWeight: 600 }}>+91 77101 17777</a> for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  const {
    patient_first_name: firstName,
    available_points,
    lifetime_points_earned,
    eligible_rewards = [],
    locked_rewards = [],
  } = data || {};

  return (
    <div style={{ minHeight: '100vh', background: BRAND.cream, ...fontBody }} data-testid="reward-wallet-public">
      {/* Editorial header */}
      <div style={{ padding: '64px 24px 32px', maxWidth: 720, margin: '0 auto' }}>
        <div style={overlineStyle} data-testid="wallet-overline">EDEN SKIN CLINIC · REWARDS</div>
        <h1 style={{ ...fontHead, fontSize: 'clamp(40px, 8vw, 64px)', lineHeight: 1.05, margin: '24px 0 8px' }} data-testid="wallet-hello">
          {firstName ? `${firstName},` : 'Welcome,'}
        </h1>
        <h2 style={{ ...fontHead, fontSize: 'clamp(24px, 5vw, 32px)', margin: 0, color: BRAND.slate }}>
          this is your rewards ledger.
        </h2>
      </div>

      {/* Balance panel */}
      <div style={{ maxWidth: 720, margin: '32px auto 0', padding: '0 24px' }}>
        <div
          style={{
            background: BRAND.white,
            border: `1px solid ${BRAND.goldSoft}`,
            padding: '48px 40px',
          }}
          data-testid="wallet-balance-card"
        >
          <div style={overlineStyle}>Available Balance</div>
          <div style={{ ...fontHead, fontSize: 'clamp(64px, 14vw, 96px)', lineHeight: 1, marginTop: 16, color: BRAND.emerald }} data-testid="wallet-balance-points">
            {Number(available_points || 0).toLocaleString('en-IN')}
            <span style={{ ...fontBody, fontSize: 18, color: BRAND.slate, marginLeft: 12, fontWeight: 400 }}>points</span>
          </div>
          <div style={{ marginTop: 24, ...fontBody, fontSize: 14, color: BRAND.slate }}>
            Lifetime earned:{' '}
            <span style={{ fontWeight: 600, color: BRAND.charcoal }}>
              {Number(lifetime_points_earned || 0).toLocaleString('en-IN')}
            </span>
          </div>
        </div>
      </div>

      {/* Next-reward nudge */}
      {data?.next_reward && (
        <div style={{ maxWidth: 720, margin: '24px auto 0', padding: '0 24px' }}>
          <div
            style={{ background: '#FFFFFF', border: `1px solid ${BRAND.gold}`, padding: '28px 32px' }}
            data-testid="wallet-next-reward-card"
          >
            <div style={overlineStyle}>Your Next Reward</div>
            <div style={{ ...fontHead, fontSize: 24, marginTop: 12 }}>
              {data.next_reward.reward_name}
              <div style={{ ...fontBody, fontSize: 14, color: BRAND.slate, marginTop: 6 }}>
                Pay ₹{Number(data.next_reward.amount_needed).toLocaleString('en-IN')} along with your{' '}
                {Number(data.available_points || available_points || 0).toLocaleString('en-IN')} points to unlock this reward
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Divider */}
      <div style={{ maxWidth: 720, margin: '64px auto 0', padding: '0 24px' }}>
        <div style={{ height: 1, background: BRAND.goldSoft }} />
      </div>

      {/* Available (unlocked) rewards */}
      <div style={{ maxWidth: 720, margin: '48px auto 0', padding: '0 24px' }}>
        <div style={overlineStyle}>Ready to Redeem</div>
        <h3 style={{ ...fontHead, fontSize: 32, margin: '12px 0 24px' }}>
          Available rewards
        </h3>
        {eligible_rewards.length === 0 ? (
          <p style={{ ...fontBody, fontSize: 15, color: BRAND.slate, lineHeight: 1.7 }}>
            Nothing to redeem just yet — keep earning and rewards will appear here as they unlock.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 16 }} data-testid="wallet-eligible-list">
            {eligible_rewards.map((r) => (
              <div
                key={r.id}
                style={{
                  background: BRAND.white,
                  border: `1px solid ${BRAND.emerald}`,
                  padding: '24px 24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 16,
                }}
                data-testid={`wallet-reward-eligible-${r.id}`}
              >
                <div>
                  <div style={{ ...fontHead, fontSize: 22, marginBottom: 4 }}>{r.reward_name}</div>
                  {r.reward_description && (
                    <div style={{ ...fontBody, fontSize: 14, color: BRAND.slate, marginTop: 6, lineHeight: 1.6 }}>
                      {r.reward_description}
                    </div>
                  )}
                  {r.linked_service_name && (
                    <div style={{ ...fontBody, fontSize: 12, color: BRAND.slate, marginTop: 12 }}>
                      Redeem against: <span style={{ color: BRAND.charcoal, fontWeight: 500 }}>{r.linked_service_name}</span>
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ ...fontBody, fontSize: 11, color: BRAND.slate, textTransform: 'uppercase', letterSpacing: '0.15em' }}>Costs</div>
                  <div style={{ ...fontHead, fontSize: 28, color: BRAND.emerald, lineHeight: 1 }}>
                    {Number(r.required_points).toLocaleString('en-IN')}
                  </div>
                  <div style={{ ...fontBody, fontSize: 11, color: BRAND.slate, marginTop: 2 }}>points</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Locked rewards */}
      {locked_rewards.length > 0 && (
        <div style={{ maxWidth: 720, margin: '64px auto 0', padding: '0 24px' }}>
          <div style={overlineStyle}>On the Horizon</div>
          <h3 style={{ ...fontHead, fontSize: 32, margin: '12px 0 24px' }}>
            Locked rewards
          </h3>
          <div style={{ display: 'grid', gap: 16 }} data-testid="wallet-locked-list">
            {locked_rewards.map((r) => (
              <div
                key={r.id}
                style={{
                  background: BRAND.sand,
                  border: `1px solid ${BRAND.goldSoft}`,
                  padding: '24px 24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 16,
                }}
                data-testid={`wallet-reward-locked-${r.id}`}
              >
                <div>
                  <div style={{ ...fontHead, fontSize: 22, marginBottom: 4, color: BRAND.slate }}>{r.reward_name}</div>
                  {r.reward_description && (
                    <div style={{ ...fontBody, fontSize: 14, color: BRAND.slate, marginTop: 6, lineHeight: 1.6 }}>
                      {r.reward_description}
                    </div>
                  )}
                  <div style={{ ...fontBody, fontSize: 12, color: BRAND.slate, marginTop: 12 }}>
                    Earn{' '}
                    <span style={{ color: BRAND.charcoal, fontWeight: 600 }}>
                      {Number(r.needs_more_points || 0).toLocaleString('en-IN')} more points
                    </span>{' '}
                    to unlock.
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ ...fontBody, fontSize: 11, color: BRAND.slate, textTransform: 'uppercase', letterSpacing: '0.15em' }}>Costs</div>
                  <div style={{ ...fontHead, fontSize: 28, color: BRAND.slate, lineHeight: 1 }}>
                    {Number(r.required_points).toLocaleString('en-IN')}
                  </div>
                  <div style={{ ...fontBody, fontSize: 11, color: BRAND.slate, marginTop: 2 }}>points</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How to redeem */}
      <div style={{ maxWidth: 720, margin: '64px auto 0', padding: '0 24px' }}>
        <div
          style={{ background: BRAND.white, border: `1px solid ${BRAND.goldSoft}`, padding: '32px' }}
          data-testid="wallet-how-to-redeem"
        >
          <div style={overlineStyle}>How to Redeem</div>
          <p style={{ ...fontBody, fontSize: 15, color: BRAND.charcoal, lineHeight: 1.8, marginTop: 12 }}>
            To redeem a reward, simply call us at{' '}
            <a href="tel:+917710117777" style={{ color: BRAND.emerald, fontWeight: 700, textDecoration: 'none' }} data-testid="wallet-redeem-phone">
              +91 77101 17777
            </a>{' '}
            and schedule your visit. Your reward will be applied at the clinic — payment, if any, is at the counter.
          </p>
          <a
            href="tel:+917710117777"
            style={{
              display: 'inline-block', marginTop: 16, padding: '12px 28px',
              background: BRAND.emerald, color: '#fff', ...fontBody,
              fontWeight: 700, fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', textDecoration: 'none',
            }}
            data-testid="wallet-call-button"
          >
            Call to Schedule
          </a>
        </div>
      </div>

      {/* Footer / Rights */}
      <div style={{ maxWidth: 720, margin: '96px auto 0', padding: '0 24px 64px' }}>
        <div style={{ height: 1, background: BRAND.goldSoft, marginBottom: 32 }} />
        <div style={overlineStyle}>Terms</div>
        <p style={{ ...fontBody, fontSize: 13, color: BRAND.slate, lineHeight: 1.7, marginTop: 12 }}>
          Points are non-transferable and non-refundable. Redemptions must be
          made in person at the clinic. Eden Skin Clinic reserves all rights,
          including the right to amend, suspend, or discontinue the rewards
          programme at any time without prior notice.
        </p>
        <p style={{ ...fontBody, fontSize: 12, color: BRAND.slate, lineHeight: 1.7, marginTop: 24, textAlign: 'center' }}>
          © Eden Skin Clinic · All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default RewardWalletPublicPage;
